import { computed } from 'vue'
import type { Ref } from 'vue'

import type { LayerTrimmer } from '@tsyl/karonte/lib/types/map'

// The incident buffer layer is added at runtime (RightBarContainer), so it isn't in the
// layers array and is listed explicitly.
const INCIDENT_BUFFER_LAYER_ID = 'incident_buffer_layer'

function sameIds(a: string[], b: string[]) {
  if (a.length !== b.length) return false
  const sortedB = [...b].sort()
  return [...a].sort().every((id, index) => id === sortedB[index])
}

// Karonte replaces (and destroys) every trimmed layer whenever the trimmer changes, so the
// previous value is reused unless the area or the set of trimmed layer ids really changed.
// Otherwise pushing the buffer layer into the layers array wipes the incidents layer off the map.
// Known limit: a layer that is genuinely added or removed at runtime still changes the trimmer,
// and Karonte still replaces every trimmed layer then; that needs a fix in Karonte itself.
export function useOrganizationAreaTrimmer(
  layers: Ref<{ id?: string }[]>,
  organizationArea: Ref<string | undefined>,
  excludedIds: (string | undefined)[]
) {
  return computed<LayerTrimmer[] | undefined>((previous) => {
    if (!organizationArea.value) return undefined
    const ids = layers.value
      .map((layer) => layer.id)
      .filter((id): id is string => !!id && id !== INCIDENT_BUFFER_LAYER_ID && !excludedIds.includes(id))
    const trimmedLayers = [...ids, INCIDENT_BUFFER_LAYER_ID]
    const [current] = previous ?? []
    if (current && current.geojson === organizationArea.value && sameIds(current.layers, trimmedLayers)) return previous
    return [{ geojson: organizationArea.value, layers: trimmedLayers }]
  })
}
